import axiosInstance from '@/Utils/axiosInstance';
import axios from '../../node_modules/axios/index';

class PatientServiceError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = 'PatientServiceError';
  }
}
const token = localStorage.getItem('token');
const language = localStorage.getItem('language');

export const patientService = {
  // Patient CRUD Operations
  getPatients: async (page, limit, searchTerm = '', filters, isSubscribed = false) => {
    try {
      const response = await axiosInstance.get(`/user/patients`, {
        params: {
          page,
          limit,
          search: searchTerm,
          ...filters,
          is_subscribed: isSubscribed,
        },
        headers: {
          Authorization: token,
          lang: language,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Get patients error:', error);
      throw new PatientServiceError('get-failed', 'Failed to get patients');
    }
  },
  getPatientById: async (externailId) => {
    try {
      const response = await axiosInstance.get(`/user/patient`, {
        params: {
          external_user_id: externailId,
        },
        headers: {
          Authorization: token,
          lang: language,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Get patient by id error:', error);
      throw new PatientServiceError('get-failed', 'Failed to get patient');
    }
  },
  updatePatient: async (patientData) => {
    try {
      const response = await axiosInstance.put(`/user/patient`, patientData, {
        headers: {
          Authorization: localStorage.getItem('token'),
          lang: language,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error Updating Patient', error);
      throw new PatientServiceError('get-failed', 'Failed to update patient');
    }
  },
  updateHealthProfile: async (patientData) => {
    try {
      const response = await axiosInstance.put(`/user/patient/health-profile`, patientData, {
        headers: {
          Authorization: localStorage.getItem('token'),
          lang: language,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error Updating Patient', error);
      throw new PatientServiceError('get-failed', 'Failed to update patient health profile');
    }
  },
  uploadHealthDocument: async (formData) => {
    try {
      const response = await axiosInstance.post(`/medicalrecords/insert_medical_record`, formData, {
        headers: {
          Authorization: localStorage.getItem('token'),
          lang: language,
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      console.error('Error Uploading Health Document', error);
    }
  },

  getPatientHealthDocuments: async (patientId) => {
    try {
      const response = await axiosInstance.get(
        `/medicalrecords/get_medical_record?lang=en&user_id=${patientId}`,
        {
          headers: {
            Authorization: localStorage.getItem('token'),
            lang: language,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error Getting Health Documents', error);
    }
  },

  getPatientMedicalRecordsPresignedUrl: async (user_id, file_name, type, action = 'preview') => {
    try {
      const data = {
        user_id: user_id,
        file_name: file_name,
        file_type: type,
        disposition: action === 'download' ? 'attachment' : 'inline',
      };

      const response = await axiosInstance.post(`/medicalrecords/medical_record_url`, data, {
        headers: {
          Authorization: localStorage.getItem('token'),
          lang: language,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error Getting Presigned URL for Medical Records', error);
      throw new PatientServiceError(
        'get-failed',
        'Failed to get presigned URL for medical records'
      );
    }
  },

  getUserSubscription: async (region, lang, package_id, plan_id) => {
    try {
      const response = await axiosInstance.get('/promocode/subscription', {
        params: {
          region,
          lang,
          package_id,
          plan_id,
        },
        headers: {
          Authorization: token,
          lang: language,
        },
      });
      return response.data;
    } catch (error) {
      throw new PatientServiceError('get-failed', 'Failed to get subscriptions');
    }
  },

  getPackages: async () => {
    try {
      const plans = await axiosInstance.get('/promocode/getAllPackages', {
        headers: {
          Authorization: token,
          lang: language,
        },
      });
      return plans.data;
    } catch (error) {
      throw new Error('Failed to fetch packages.');
    }
  },

  getUserPlans: async (user_id) => {
    try {
      const data = {
        user_id: user_id,
      };

      const response = await axios.post(
        `${import.meta.env.VITE_DRU_PATIENT_APP_BASE_URL}/users-owned-offers`,
        data,
        {
          headers: {
            'x-access-key': `${import.meta.env.VITE_REACT_APP_X_ACCESS_KEY}`,
            Authorization: token,
            lang: language,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to get plans:', error);
      return { body: [] };
    }
  },

  fetchCompanionMessages: async (user_id) => {
    try {
      const data = {
        user_id: user_id,
        isFromTool: true,
      };
      const response = await axios.post(
        `${import.meta.env.VITE_DRU_PATIENT_APP_BASE_URL}/notifications/get_user_companion_messages`,
        data,
        {
          headers: {
            'x-access-key': `${import.meta.env.VITE_REACT_APP_X_ACCESS_KEY}`,
            Authorization: token,
            lang: language,
          },
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching companion messages', error);
    }
  },

  sendCompanionMessage: async (user_id, messageData) => {
    const { body } = messageData;
    const data = {
      user_id: user_id,
      title: 'companion',
      body: body,
    };
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_DRU_PATIENT_APP_BASE_URL}/notifications/send_notification`,
        data,
        {
          headers: {
            'x-access-key': `${import.meta.env.VITE_REACT_APP_X_ACCESS_KEY}`,
            Authorization: token,
            lang: language,
          },
        }
      );
      return response;
    } catch (error) {
      console.error('Error sending companion message', error);
    }
  },

  subscribe: async (user_id, payment_plan_id, is_free_trial = false) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('user_id', user_id.toString());
      queryParams.append('payment_plan_id', payment_plan_id.toString());
      queryParams.append('is_free_trial', is_free_trial ? 'true' : 'false');

      const response = await axios.post(
        `${import.meta.env.VITE_DRU_PATIENT_APP_BASE_URL}/users/subscribe_for_free?${queryParams.toString()}`,
        {
          user_id,
        },
        {
          headers: {
            'x-access-key': import.meta.env.VITE_REACT_APP_X_ACCESS_KEY,
            Authorization: token,
            lang: language,
          },
        }
      );
      return response.data;
    } catch (error) {
      return error.message;
    }
  },
  uploadData: async (formData, setLoading) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post('/plan/upload_file', formData, {
        headers: {
          authorization: `${localStorage.getItem('token')}`,
        },
      });
      return response.data;
    } catch (error) {
      throw new Error('Failed to upload data.');
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  },
  getUserDocs: async (setLoading, setUploadError, user_id, plan_id) => {
    try {
      const data = {
        external_user_id: user_id,
        offer_id: plan_id,
      };
      setLoading(true);
      const response = await axiosInstance.get('/plan/get_personliazed_plans_documents', {
        params: data,
        headers: {
          Authorization: token,
          lang: language,
        },
      });
      setLoading(false);
      return response.data;
    } catch (error) {
      setLoading(false);
      setUploadError(error.message || 'An error occurred');
      return error.message;
    }
  },
  deleteDocument: async (
    _id,
    setDeleteError,
    setDeleteSuccess,
    setUploadError,
    setUploadSuccess,
    setLoading
  ) => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete('/plan/deletefile', {
        data: { file_id: _id }, // Send file ID to delete
      });
      setDeleteSuccess(response.data.message);
      setDeleteError('');
      setUploadError('');
      setUploadSuccess('');
      return response.data;
    } catch (error) {
      setDeleteError(error.message || 'An error occurred');
      setDeleteSuccess('');
      setUploadError('');
      setUploadSuccess('');
      throw new Error('Failed to delete document.');
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  },

  getUserMeasurments: async (params) => {
    try {
      const response = await axiosInstance.get(`/measurments/health_trends`, {
        params: {
          ...params,
        },
        headers: {
          Authorization: token,
          lang: language,
        },
      });
      return response;
    } catch (error) {
      console.error('Error getting user Measurments', error);
      throw error;
    }
  },
  getExpertNotesByDoctor: async (external_user_id) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('external_user_id', external_user_id.toString());

      const resposne = await axiosInstance.get(`/user-notes/by-doctor?${queryParams.toString()}`, {
        headers: {
          'x-access-key': import.meta.env.VITE_REACT_APP_X_ACCESS_KEY,
          lang: localStorage.getItem('language'),
        },
      });
      return resposne.data;
    } catch (error) {
      console.error('Error getting user expert notes.', error);
    }
  },
  getExpertNotesDetailsByDoctor: async (external_user_id, doctor_id) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('external_user_id', external_user_id.toString());
      queryParams.append('doctor_id', doctor_id.toString());

      const response = await axiosInstance.get(`/expert-notes/doctor?${queryParams.toString()}`, {
        headers: {
          Authorization: token,
          lang: language,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },
  getAllDoctors: async () => {
    try {
      const response = await axiosInstance.get('/doctorsappuser/alldoctors', {
        headers: {
          Authorization: token,
          lang: language,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  PlanDuration: [
    {
      value: 'weekly',
      duration: 7,
    },
    {
      value: 'biweekly',
      duration: 15,
    },
    {
      value: 'monthly',
      duration: 30,
    },
    {
      value: 'two_months',
      duration: 60,
    },
    {
      value: 'quarterly',
      duration: 90,
    },
    {
      value: 'half_annual',
      duration: 180,
    },
    {
      value: 'annually',
      duration: 360,
    },
  ],

  getFileUrl: async (file_name, user_id) => {
    try {
      const data = { file_name, user_id };
      const response = await axiosInstance.post(
        `${import.meta.env.VITE_DRU_PATIENT_APP_BASE_URL}/users-owned-offers/offer_file_url`,
        data,
        {
          headers: {
            'x-access-key': import.meta.env.VITE_REACT_APP_X_ACCESS_KEY,
            lang: language,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },

  getSubscriptionPlans: async () => {
    try {
      const response = await axiosInstance.get('/payment-plans', {
        headers: {
          Authorization: token,
          lang: language,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error response:', error.response?.data);
      throw error;
    }
  },
};
